import type { ChangeEvent } from 'react';
import {
  array as rawArray,
  object as rawObject,
  scalar as rawScalar,
} from '@magical-forms/react-next';

import type {
  ArrayField,
  Field,
  FormState,
  FormStateFromFieldsObj,
  FormValue,
  FormValueFromFieldsObj,
  ObjectField,
  ScalarField,
  ScalarState,
  ScalarValidationFn,
  ValidationFnInObjectValidation,
  ValidationObj,
} from './magicalFormsTypes';

type ScalarInput<Value> = {
  readonly value: Value;
  readonly touched: boolean;
  readonly onBlur: () => void;
  readonly onFocus: () => void;
  readonly onChange: (value: Value) => void;
} & (
  | {
      readonly validity: 'valid';
      readonly error?: string;
    }
  | {
      readonly validity: 'invalid';
      readonly error: string;
    }
);

type ScalarOptionsBase<Value> =
  | {
      validate?: ScalarValidationFn<Value, Value>;
      stateFromChange?: (
        next: ScalarState<Value>,
        current?: ScalarState<Value>
      ) => ScalarState<Value>;
    }
  | undefined;

type ValidationFnFromOptions<
  Value,
  Options extends ScalarOptionsBase<Value>
> = Options extends {
  validate?: infer Validation;
}
  ? Validation extends ScalarValidationFn<Value, Value>
    ? Validation
    : ScalarValidationFn<Value, Value>
  : ScalarValidationFn<Value, Value>;

type ArrayFieldValidation<
  Element extends Field,
  Value
> = Element extends ScalarField
  ? ValidationFnInObjectValidation<Element, Value>
  : Element extends ObjectField<any>
  ? ValidationObj<Element['fields'], Value>
  : never | undefined;

function scalar<Value, InitialValueInput, Props>(scalarFieldOptions: {
  props: (input: ScalarInput<Value>) => Props;
  initialValue: (initialValueInput: InitialValueInput) => Value;
}) {
  const rawField = rawScalar(scalarFieldOptions);

  return <Options extends ScalarOptionsBase<Value>>(options?: Options) => {
    return (rawField(options) as unknown) as ScalarField<
      Value,
      InitialValueInput,
      ValidationFnFromOptions<Value, Options>,
      Props
    >;
  };
}

export function array<Element extends Field>(
  element: Element,
  options?: {
    validate?: ArrayFieldValidation<Element, readonly FormValue<Element>[]>;
    stateFromChange?: (
      next: readonly FormState<Element>[],
      current?: readonly FormState<Element>[]
    ) => readonly FormState<Element>[];
  }
): ArrayField<Element> {
  return rawArray(element, options as any) as ArrayField<Element>;
}

export const object = <
  Fields extends { readonly [Key in keyof Fields]: Field }
>(
  fields: Fields,
  options?: {
    validate?: ValidationObj<Fields, FormValueFromFieldsObj<Fields>>;
    stateFromChange?: (
      next: FormStateFromFieldsObj<Fields>,
      current?: FormStateFromFieldsObj<Fields>
    ) => FormStateFromFieldsObj<Fields>;
  }
): ObjectField<Fields> => {
  return rawObject(fields, options);
};

function validationToFieldProps<
  Obj extends {
    validity: 'valid' | 'invalid';
    error?: string;
    touched: boolean;
  }
>({
  error,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  validity,
  touched,
  ...obj
}: Obj): Omit<Obj, 'error' | 'validity' | 'touched'> & {
  invalidMessage?: string;
} {
  if (touched) {
    return {
      invalidMessage: error,
      ...obj,
    };
  }
  return obj;
}

export const multiselect = scalar({
  props: (field) => {
    return {
      ...validationToFieldProps(field),
      blur: () => {
        field.onBlur();
      },
      onBlur: undefined,
    };
  },
  initialValue: (input: string[] | undefined) => {
    return input || [];
  },
});

export const text = scalar({
  props: ({ onChange, ...field }) => {
    return {
      ...validationToFieldProps(field),
      onChange(event: ChangeEvent<HTMLInputElement>) {
        onChange(event.target.value);
      },
      blur: () => {
        field.onBlur();
      },
      onBlur: undefined,
    };
  },
  initialValue: (input: string | undefined | null) => {
    return input || '';
  },
});

export const multilineText = scalar({
  props: ({ onChange, ...field }) => {
    return {
      ...validationToFieldProps(field),
      onChange(event: ChangeEvent<HTMLTextAreaElement>) {
        onChange(event.target.value);
      },
      blur: () => {
        field.onBlur();
      },
      onBlur: undefined,
    };
  },
  initialValue: (input: string | undefined | null) => {
    return input || '';
  },
});

export const email = scalar({
  props: ({ onChange, ...field }) => {
    return {
      ...validationToFieldProps(field),
      inputMode: 'email' as const,
      onChange(event: ChangeEvent<HTMLInputElement>) {
        onChange(event.target.value);
      },
      blur: () => {
        field.onBlur();
      },
      onBlur: undefined,
    };
  },
  initialValue: (input: string | undefined | null) => {
    return input || '';
  },
});

// export const number = scalar({
//   props: ({ onChange, validity, error, value, ...field }) => ({
//     ...field,
//     value: value === undefined ? '' : '' + value,
//     invalidMessage: error,
//     onChange(event: ChangeEvent<HTMLInputElement>) {
//       onChange(
//         event.target.value === '' ? undefined : Number(event.target.value)
//       );
//     },
//   }),
//   initialValue: (input: number | undefined) => input,
// });

export const currency = scalar({
  props: (field) => {
    return {
      ...validationToFieldProps(field),
      blur: () => {
        field.onBlur();
      },
      onBlur: undefined,
    };
  },
  initialValue: (input: string | number | undefined | null) => {
    return input === null || input === undefined ? '' : input;
  },
});

export const duration = scalar({
  props: ({ onChange, ...field }) => {
    return {
      ...validationToFieldProps(field),
      onChange(value?: string | number) {
        onChange(value !== undefined ? value : '');
      },
      blur: () => {
        field.onBlur();
      },
      onBlur: undefined,
    };
  },
  initialValue: (input: string | number | undefined | null) => {
    return input || '';
  },
});

export const boolean = scalar({
  props: (field) => {
    return {
      ...validationToFieldProps(field),
      blur: () => {
        field.onBlur();
      },
      onBlur: undefined,
    };
  },
  initialValue: (input: boolean | undefined | null) => {
    return input || false;
  },
});

export const string = scalar({
  props: (field) => {
    return {
      ...validationToFieldProps(field),
      blur: () => {
        field.onBlur();
      },
      onBlur: undefined,
    };
  },
  initialValue: (input: string | undefined | null) => {
    return input ?? undefined;
  },
});

// TODO: remove these dates from here and import from a non jsx rendering date orientated package
type ISODate = string & { ___ISODateString: true };

type ISODateRange = { start?: ISODate; end?: ISODate };

export const date = scalar({
  props: (input) => {
    return {
      ...validationToFieldProps(input),
      onClear() {
        input.onChange(undefined);
      },
      blur: () => {
        input.onBlur();
      },
      onBlur: undefined,
    };
  },
  initialValue: (input: ISODate | undefined | null) => {
    return input ?? undefined;
  },
});

export const dateRange = scalar({
  props: (field) => {
    return {
      ...validationToFieldProps(field),
      blur: () => {
        field.onBlur();
      },
      onBlur: undefined,
    };
  },
  initialValue: (input: ISODateRange | undefined | null) => {
    return input ?? undefined;
  },
});

export const empty = scalar({
  props: () => {
    return undefined;
  },
  initialValue: (input: undefined) => {
    return input;
  },
});

export const select = <Value extends string>() => {
  return scalar({
    props: (field) => {
      return {
        ...validationToFieldProps(field),
        blur: () => {
          field.onBlur();
        },
        onBlur: undefined,
      };
    },
    initialValue: (input: Value | undefined | null) => {
      return input ?? undefined;
    },
  });
};

export const autocomplete = <
  Option extends { label: string; value: string }
>() => {
  return scalar({
    props: (field) => {
      return {
        ...validationToFieldProps(field),
        blur: () => {
          field.onBlur();
        },
        onBlur: undefined,
      };
    },
    initialValue: (input: Option | undefined | null) => {
      return input ?? null;
    },
  });
};

const field = {
  object,
  array,
  autocomplete,
  select,
  empty,
  dateRange,
  date,
  string,
  boolean,
  duration,
  currency,
  email,
  text,
  multilineText,
  multiselect,
};

// export as composites
export { scalar, field };
